// ============================================================================
// acoes.js — CATÁLOGO DE AÇÕES (segundo eixo de permissão)
// ----------------------------------------------------------------------------
// `menu_key` responde "esta tela aparece?". AÇÃO responde "dentro da tela, esta
// pessoa pode fazer isto?" — editar registro travado, excluir, ver valores,
// aba de diagnóstico. Antes isso vivia hardcoded na tela:
//
//   const podeEditarAprovado = permissions.admin || permissions.gGeral
//                            || permissions.dev || permissions.supDoc;
//
// Agora:
//
//   const { podeAcao } = useAuth();
//   const podeEditarAprovado = podeAcao('saldos_patios.editar_aprovado');
//
// ARMAZENAMENTO: mesma tabela `operacional.tipo_usuario_permissoes`, com a key
// prefixada por `acao:` (ex.: `acao:saldos_patios.editar_aprovado`). Zero DDL
// novo — a tabela não tem FK para menu_itens.
//
// `roles` = quem podia ANTES da migração. É o fallback offline e o seed.
// Depois de rodar o seed, quem manda é a tela /manutencao/papeis → aba Ações.
//
// REGRA: nunca renomeie uma `key` já existente (quebra o vínculo com o banco).
//
// ESTADO: todo gate vivo de página já usa `podeAcao` — marcar/desmarcar na aba
// Ações da tela de papéis surte efeito. O que falta é o `requireAcao` nas rotas
// da API: hoje a ação esconde o botão, não trava a chamada. Ver RBAC.md.
// ============================================================================

/** Prefixo que separa ação de item de menu dentro de tipo_usuario_permissoes. */
export const ACAO_PREFIX = 'acao:';

export const ACOES = [
  // ---- Logística / Estimativa -------------------------------------------
  {
    key: 'logistica.editar_estimativa',
    grupo: 'Logística',
    label: 'Editar estimativa',
    descricao: 'Alterar dados da estimativa no Painel de Logística.',
    // O código checava `permissions.adm` (typo, nunca verdadeiro) junto de
    // supDoc/gGeral/dev. Aqui o admin entra de verdade.
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },
  {
    key: 'logistica.ver_valores',
    grupo: 'Logística',
    label: 'Ver valores da estimativa',
    descricao: 'Exibir os valores financeiros dentro do detalhe da estimativa.',
    roles: ['dev', 'financeiro'],
  },
  {
    key: 'logistica.gerenciar_fotos',
    grupo: 'Logística',
    label: 'Gerenciar fotos da vistoria',
    descricao: 'Enviar, substituir e excluir fotos de antes/depois na estimativa.',
    roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc'],
  },
  {
    key: 'logistica.enviar_fotos_edi',
    grupo: 'Logística',
    label: 'Enviar fotos do processo para EDI',
    descricao: 'Botão que processa/reprocessa o envio das fotos do processo ao EDI do armador.',
    roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc'],
  },
  {
    key: 'vistorias.cancelar',
    grupo: 'Logística',
    label: 'Cancelar vistoria',
    descricao: 'Cancelar uma vistoria no Painel de Vistorias.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },

  // ---- Agendamento -------------------------------------------------------
  {
    key: 'agendamento.editar_agendamento',
    grupo: 'Agendamento',
    label: 'Editar agendamento',
    descricao: 'Alterar dados de um agendamento já criado.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento', 'documental'],
  },
  {
    key: 'agendamento.editar_vistoria',
    grupo: 'Agendamento',
    label: 'Editar vistoria do agendamento',
    descricao: 'Abrir e alterar o bloco de vistoria dentro do agendamento.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
  },
  {
    key: 'agendamento.editar_campos_restritos',
    grupo: 'Agendamento',
    label: 'Editar campos restritos do agendamento',
    descricao: 'Status, pátio e opção de vistoria — campos que o colaborador comum não altera.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
  },
  {
    key: 'agendamento.prazo_estendido',
    grupo: 'Agendamento',
    label: 'Agendar com prazo estendido (60 dias)',
    descricao: 'Sem esta ação o limite para marcar data é de 2 dias à frente.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
  },
  {
    key: 'agendamento.excluir_booking',
    grupo: 'Agendamento',
    label: 'Excluir booking',
    descricao: 'Remover um booking da tela de Bookings.',
    roles: ['dev', 'admin', 'supDoc'],
  },
  {
    key: 'agendamento.ver_janela_hora',
    grupo: 'Agendamento',
    label: 'Ver janela por hora',
    descricao: 'Exibir a coluna/visão de janela por hora no Painel de Agendamento.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },

  // ---- Devoluções --------------------------------------------------------
  {
    key: 'devolucoes.editar',
    grupo: 'Devoluções',
    label: 'Editar devolução',
    descricao: 'Habilita a edição dos campos no modal de devolução (origem painel/consulta).',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
  },

  // ---- Avaria ------------------------------------------------------------
  {
    key: 'avaria.limpar_dados_fiscais',
    grupo: 'Avaria',
    label: 'Limpar dados fiscais da cobrança',
    descricao: 'Botão que zera os dados fiscais da cobrança para o cliente reinformar. Bloqueado quando a cobrança já está paga/faturada.',
    roles: ['dev', 'supAva'],
  },
  {
    key: 'avaria.verifica_na',
    grupo: 'Avaria',
    label: 'Verifica N/A (Lançamento de Cobranças)',
    descricao: 'Varre unidades sem agendamento vinculado (Espec_In sem "Agend N"), grava o vínculo e cadastra devolução Rio Claro/Suzano pré-preenchida. Backend com lista fixa: dev/admin/gGeral/supAva.',
    roles: ['dev', 'admin', 'gGeral', 'supAva'],
  },
  {
    key: 'avaria.montar_lote',
    grupo: 'Avaria',
    label: 'Montar cobrança em lote',
    descricao: 'Criar lote, adicionar/remover cobranças e cancelar lote na tela Cobranças em Lote. Travado também no backend (python, utils/rbac.py).',
    roles: ['dev'],
  },

  // ---- Documental --------------------------------------------------------
  {
    key: 'documental.faturar_isencao',
    grupo: 'Documental',
    label: 'Definir isenção (D→I)',
    descricao: 'Isenções à Definir: marcar isenções como definidas (Pagam D → I). Backend com lista fixa (TIPOS_USUARIO_FATURAM).',
    roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc'],
  },

  // ---- Gate --------------------------------------------------------------
  {
    key: 'gate.liberar_restricao',
    grupo: 'Gate',
    label: 'Liberar restrição de entrada',
    descricao: 'Libera container com restrição (pré-cadastro "não receber") para o gate-in. Backend com lista fixa: dev/admin/gGeral.',
    roles: ['dev', 'admin', 'gGeral'],
  },

  // ---- Gerenciar ---------------------------------------------------------
  {
    key: 'saldos_patios.editar_aprovado',
    grupo: 'Gerenciar',
    label: 'Editar saldo já aprovado',
    descricao: 'Destravar e corrigir um saldo de pátio depois da aprovação.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },

  // ---- Cadastros ---------------------------------------------------------
  {
    key: 'cadastros.criar_entidade',
    grupo: 'Cadastros',
    label: 'Criar entidade',
    descricao: 'Botão de nova entidade na consulta de entidades.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },
  {
    key: 'cadastros.editar_entidade',
    grupo: 'Cadastros',
    label: 'Editar entidade',
    descricao: 'Habilitar a edição dos dados no modal de entidade.',
    roles: ['dev', 'admin', 'gGeral'],
  },

  // ---- Relatórios --------------------------------------------------------
  {
    key: 'relatorios.detalhes_devolucoes',
    grupo: 'Relatórios',
    label: 'Ver detalhes no Dashboard de Devoluções',
    descricao: 'Abrir o drill-down por linha do dashboard de devoluções.',
    roles: ['dev', 'admin', 'gGeral', 'supDoc'],
  },
  {
    key: 'relatorios.producao_avaria',
    grupo: 'Relatórios',
    label: 'Ver produção no Dashboard de Avarias',
    descricao: 'Exibir os números de produção por colaborador no dashboard de avarias.',
    roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
  },

  // ---- Configurações -----------------------------------------------------
  {
    key: 'config.editar_desenvolvimento',
    grupo: 'Configurações',
    label: 'Editar quadro de desenvolvimento',
    descricao: 'Criar e alterar cartões na tela de Desenvolvimento (só leitura sem isto).',
    roles: ['dev'],
  },
  {
    key: 'config.comprovante_ia',
    grupo: 'Configurações',
    label: 'Leitura de comprovante por IA',
    descricao: 'Ver e alterar, em Manutenção > Leitura de Comprovante (IA): liga/desliga, provedor (Gemini/Claude), reserva e chaves. A chave só aparece mascarada. Travado também no backend (python, utils/rbac.py).',
    roles: ['dev'],
  },
  {
    key: 'config.pix',
    grupo: 'Configurações',
    label: 'Pagamento por PIX no portal',
    descricao: 'Ver e ligar/desligar, em Manutenção > Pagamento por PIX, o botão "Pagar com PIX" do portal de cobrança. Travado também no backend (python, utils/rbac.py).',
    roles: ['dev'],
  },
];

/** key -> ação. */
export const ACAO_INDEX = ACOES.reduce((acc, a) => {
  acc[a.key] = a;
  return acc;
}, {});

/** Grupos na ordem em que aparecem no catálogo. */
export const ACAO_GRUPOS = [...new Set(ACOES.map((a) => a.grupo))];

/** Como a ação é gravada em tipo_usuario_permissoes.menu_key. */
export const acaoKey = (key) => `${ACAO_PREFIX}${key}`;

/**
 * Permissões de ação padrão por papel: { roleKey: Set<'acao:...'> }.
 * Fallback quando a API não responde, e origem do seed.
 */
export function defaultAcoesByRole() {
  const byRole = {};
  ACOES.forEach((a) => {
    (a.roles || []).forEach((role) => {
      (byRole[role] || (byRole[role] = new Set())).add(acaoKey(a.key));
    });
  });
  return byRole;
}

export default ACOES;
